import LegacyRouter from "components/Router/router";
import { CONTACT, PRODUCT, PROSPECT, QUOTE, TASK } from "v4/data/entities";
import ContactsPage from "v4/pages/front/Contacts/ContactsPage";
import DashboardPage from "v4/pages/front/Dashboard/DashboardPage";
import MailsTypesPage from "v4/pages/front/MailsTypes/MailsTypesPage";
import NewsletterPage from "v4/pages/front/Newsletter/NewsletterPage";
import OAuthPage from "v4/pages/front/OAuth/OAuthPage";
import ProductsPage from "v4/pages/front/Products/ProductsPage";
import ProspectPage from "v4/pages/front/Prospect/ProspectPage";
import ProspectsPage from "v4/pages/front/Prospects/ProspectsPage";
import QuotesPage from "v4/pages/front/Quotes/QuotesPage";
import SearchPage from "v4/pages/front/Search/SearchPage";
import TasksPage from "v4/pages/front/Tasks/TasksPage";

/* Legacy Admin Components imports */
import Ged from "views/Ged/Ged";

/**
 * @typedef {Object} ServerNavigationItem
 * @property {number} id
 * @property {string|null} native_identifier
 * @property {string} label
 * @property {string|null} url
 * @property {string|null} slug
 * @property {string|null} icon
 * @property {string|null} search_type
 * @property {string|null} search_content
 * @property {ServerNavigationItem[]} children
 * @property {Object} metadata
 * @property {string|null} metadata.badge_id
 */

/**
 * @typedef {Object} NavigationItem
 * @property {string} id
 * @property {string|null} identifier
 * @property {string} label
 * @property {string|null} icon
 * @property {string|null} url
 * @property {string|null} slug
 * @property {string} className
 * @property {Object|null} searchContent
 * @property {string|null} searchType
 * @property {string|null} badgeId
 * @property {NavigationItem[]} children
 */

/**
 * Common additional paths for DETAIL views (from appRoutes.js)
 */
const DETAIL_ADDITIONAL_PATHS = {
    [TASK]: [
        { path: 'add', routeType: 'ADD' },
        { path: '{task}', routeType: 'SHOW' },
        { path: '{task}/edit', routeType: 'EDIT' }
    ],
    [CONTACT]: [
        { path: 'add', routeType: 'ADD' },
        { path: 'mail', routeType: 'MAIL' },
        { path: '{contact}', routeType: 'SHOW' },
        { path: '{contact}/edit', routeType: 'EDIT' }
    ],
    [QUOTE]: [
        { path: 'add', routeType: 'ADD' },
        { path: '{quote}', routeType: 'SHOW' },
        { path: '{quote}/edit', routeType: 'EDIT' }
    ],
};

export const NAVIGATION_MAPPER = {
    'main_oauth': {
        component: OAuthPage,
        className: 'oauth',
        isExternal: true,
        isProtected: true,
        voter: null,
        additionalPaths: [
            { path: 'authorize', routeType: 'AUTHORIZE' },
        ],
    },
    'main_search': {
        component: SearchPage,
        className: 'recherche',
        isExternal: false,
        isProtected: true,
        voter: null,
    },
    'main_dashboard': {
        component: DashboardPage,
        className: 'dashboard',
        isExternal: false,
        isProtected: true,
        voter: null,
    },
    'main_prospects': {
        component: ProspectsPage,
        className: 'prospects',
        isExternal: false,
        isProtected: true,
        voter: null,
        additionalPaths: [
            { path: 'add/business', routeType: 'ADD_BUSINESS' },
            { path: 'add/individual', routeType: 'ADD_INDIVIDUAL' },
            { path: 'customer-code/{customerCode}', routeType: 'CUSTOMER_CODE' },
            { path: '{alternativeListing}', routeType: 'ALTERNATIVE_LISTING' },
        ],
        alternatives: ['maps', 'statistics'],
    },
    'main_contacts': {
        component: ContactsPage,
        className: 'contacts',
        isExternal: false,
        isProtected: true,
        voter: null,
    },
    'main_tasks': {
        component: TasksPage,
        className: 'actions',
        isExternal: false,
        isProtected: true,
        voter: null,
    },
    'main_quotes': {
        component: QuotesPage,
        className: 'devis',
        isExternal: false,
        isProtected: true,
        voter: null,
    },
    'main_mails_types': {
        component: MailsTypesPage,
        className: 'mails',
        isExternal: false,
        isProtected: true,
        voter: null,
        additionalPaths: [
            { path: 'add', routeType: 'ADD' },
            { path: '{mailType}/edit', routeType: 'EDIT' }
        ],
    },
    'main_mails_newsletter_create': {
        component: NewsletterPage,
        className: 'mails',
        isExternal: false,
        isProtected: true,
        voter: null,
        additionalPaths: [
            { path: 'add', routeType: 'ADD' }
        ],
    },
    'main_mails_newsletter_manage': {
        component: NewsletterPage,
        className: 'mails',
        isExternal: false,
        isProtected: true,
        voter: null,
        additionalPaths: [
            { path: 'gestions', routeType: 'LISTING' }
        ],
    },
    'main_mails_newsletter_journal': {
        component: NewsletterPage,
        className: 'mails',
        isExternal: false,
        isProtected: true,
        voter: null,
        additionalPaths: [
            { path: 'journal', routeType: 'LISTING' }
        ],
    },
    'main_mails_newsletter_stats': {
        component: NewsletterPage,
        className: 'mails',
        isExternal: false,
        isProtected: true,
        voter: null,
        additionalPaths: [
            { path: 'statistiques', routeType: 'SHOW' }
        ],
    },
    'main_mails_import': {
        component: NewsletterPage,
        className: 'mails',
        isExternal: false,
        isProtected: true,
        voter: null,
        additionalPaths: [
            { path: 'import-destinataires', routeType: 'ADD' }
        ],
    },
    'main_newsletter_emailing': {
        component: NewsletterPage,
        className: 'mails',
        isExternal: false,
        isProtected: true,
        voter: null,
    },
    'main_ged': {
        component: Ged,
        className: 'ged',
        isExternal: false,
        isProtected: true,
        voter: null,
    },
    'main_products': {
        component: ProductsPage,
        className: 'articles',
        isExternal: false,
        isProtected: true,
        voter: null,
        entity: PRODUCT,
        additionalPaths: [
            { path: 'add', routeType: 'ADD' },
            { path: '{article}/edit', routeType: 'EDIT' }
        ],
    },

    // Detail routes
    'detail_contacts': {
        component: ProspectPage,
        className: 'contacts',
        isExternal: false,
        isProtected: true,
        voter: null,
        groupCode: 'prospect',
        additionalPaths: DETAIL_ADDITIONAL_PATHS[CONTACT],
    },
    'detail_open_tasks': {
        component: ProspectPage,
        className: 'actions',
        isExternal: false,
        isProtected: true,
        voter: null,
        groupCode: 'prospect',
        additionalPaths: DETAIL_ADDITIONAL_PATHS[TASK],
    },
    'detail_closed_tasks': {
        component: ProspectPage,
        className: 'historique',
        isExternal: false,
        isProtected: true,
        voter: null,
        groupCode: 'prospect',
        additionalPaths: DETAIL_ADDITIONAL_PATHS[TASK],
    },
    'detail_mail_history': {
        component: ProspectPage,
        className: 'historique-mails',
        isExternal: false,
        isProtected: true,
        voter: null,
        groupCode: 'prospect',
        additionalPaths: DETAIL_ADDITIONAL_PATHS[TASK],
    },
    'detail_received_mails': {
        component: ProspectPage,
        className: 'received-mails',
        isExternal: false,
        isProtected: true,
        voter: null,
        groupCode: 'prospect',
        additionalPaths: DETAIL_ADDITIONAL_PATHS[TASK],
    },
    'detail_quotes': {
        component: ProspectPage,
        className: 'devis-offres',
        isExternal: false,
        isProtected: true,
        voter: null,
        groupCode: 'prospect',
        additionalPaths: DETAIL_ADDITIONAL_PATHS[QUOTE],
    },
    'detail_won_quotes': {
        component: ProspectPage,
        className: 'affaires',
        isExternal: false,
        isProtected: true,
        voter: null,
        groupCode: 'prospect',
        additionalPaths: DETAIL_ADDITIONAL_PATHS[QUOTE],
    },
    'detail_stats_360': {
        component: ProspectPage,
        className: 'stats-360',
        isExternal: false,
        isProtected: true,
        voter: null,
        groupCode: 'prospect',
        additionalPaths: [
            { path: 'product-view', routeType: 'PRODUCT_VIEW' }
        ],
    },

    /***** STATICS (not from server) *****/
    'main_not_found': {
        component: LegacyRouter,
        className: '404',
        isExternal: false,
        isProtected: true,
        voter: null,
    },

    // Detail routes
    'detail_maps': {
        component: ProspectPage,
        className: 'maps',
        isExternal: false,
        isProtected: true,
        voter: null,
        groupCode: 'prospect',
    },
    'detail_resume_tasks': {
        component: ProspectPage,
        className: 'actions',
        isExternal: false,
        isProtected: true,
        voter: null,
        groupCode: 'prospect',
    },
    'detail_not_found': {
        component: ProspectPage,
        className: '404',
        isExternal: false,
        isProtected: true,
        voter: null,
        groupCode: 'prospect',
    },
};

export const MAIN_PROPERTIES_FROM_SEARCH_TYPE = {
    [TASK]: {
        ...NAVIGATION_MAPPER['main_tasks'],
    },
    [CONTACT]: {
        ...NAVIGATION_MAPPER['main_contacts'],
    },
    [QUOTE]: {
        ...NAVIGATION_MAPPER['main_quotes'],
    },
    [PROSPECT]: {
        ...NAVIGATION_MAPPER['main_prospects'],
    },
};

export const DETAIL_PROPERTIES_FROM_SEARCH_TYPE = {
    [TASK]: {
        ...NAVIGATION_MAPPER['detail_open_tasks'],
        additionalPaths: DETAIL_ADDITIONAL_PATHS[TASK],
    },
    [CONTACT]: {
        ...NAVIGATION_MAPPER['detail_contacts'],
        additionalPaths: DETAIL_ADDITIONAL_PATHS[CONTACT],
    },
    [QUOTE]: {
        ...NAVIGATION_MAPPER['detail_quotes'],
        additionalPaths: DETAIL_ADDITIONAL_PATHS[QUOTE],
    },
};

/**
 * Résout la configuration d'une route à partir d'un item de navigation serveur.
 * @param {ServerNavigationItem} item
 * @param {boolean} isDetailContext - Si true, on cherche une config de type détail
 */
export function resolveRouteComponent(item, isDetailContext = false) {
    if (!item) return null;

    // 1. Priorité à l'identifiant natif (qui est explicite : main_... ou detail_...)
    if (item.native_identifier && NAVIGATION_MAPPER[item.native_identifier]) {
        return NAVIGATION_MAPPER[item.native_identifier];
    }

    // 2. Fallback sur le type de recherche (search_type) selon le contexte
    const searchTypeRules = isDetailContext ? DETAIL_PROPERTIES_FROM_SEARCH_TYPE : MAIN_PROPERTIES_FROM_SEARCH_TYPE;

    if (item.search_type && searchTypeRules[item.search_type]) {
        return searchTypeRules[item.search_type];
    }

    return null;
}
